// "Pick up where you left off" chip on koi collection pages
// (snippets/ck-resume-view.liquid). Whenever a shopper leaves a collection on
// a filtered or sorted view, that view's query string is remembered in the
// browser; landing on the same collection later with no filters shows one
// dismissible chip offering to reapply it. Nothing is ever auto-applied.
(function () {
  if (customElements.get('ck-resume-view')) return;

  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // saved views expire after 30 days
  const MAX_LABEL_PARTS = 3;

  const SORT_LABELS = {
    'price-ascending': 'price low to high',
    'price-descending': 'price high to low',
    'created-descending': 'newest first',
    'created-ascending': 'oldest first',
    'best-selling': 'best selling',
    'title-ascending': 'A to Z',
    'title-descending': 'Z to A',
    manual: 'featured',
  };

  // Keep only the params that define the view: facet filters and sort.
  // Pagination, tracking params, and cache busters are deliberately dropped so
  // nobody resumes onto page 7 of a grid that has since turned over.
  function sanitize(search) {
    const kept = new URLSearchParams();
    new URLSearchParams(search).forEach((value, key) => {
      if (key.indexOf('filter.') === 0 || key === 'sort_by') kept.append(key, value);
    });
    return kept.toString();
  }

  function formatPrice(value) {
    const amount = parseFloat(value);
    if (Number.isNaN(amount)) return null;
    return '$' + Math.round(amount).toLocaleString('en-US');
  }

  // Human-readable summary of a saved query string, e.g.
  // "Showa · under $3,500 · price low to high". Unknown facets fall back to
  // their raw values, so a facet added later still reads acceptably.
  function describe(queryString) {
    const params = new URLSearchParams(queryString);
    const parts = [];
    const seen = new Set();
    let priceMin = null;
    let priceMax = null;
    let priceSlot = -1;
    let sortLabel = null;

    params.forEach((value, key) => {
      if (key === 'sort_by') {
        sortLabel = SORT_LABELS[value] || value.replace(/-/g, ' ');
        return;
      }
      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        if (key.slice(-3) === 'gte') priceMin = value;
        else priceMax = value;
        if (priceSlot < 0) priceSlot = parts.length;
        return;
      }
      if (key === 'filter.v.availability' || key === 'filter.p.availability') {
        if (!seen.has('availability')) {
          seen.add('availability');
          parts.push('sold hidden');
        }
        return;
      }
      const facet = key.split('.').pop();
      let label = value;
      if (facet === 'age' && /^\d+$/.test(value)) label = value + ' yr';
      if (facet === 'sex' && value === 'Not Checked') label = 'Unsexed';
      // Search & Discovery value groups arrive as an opaque group id
      if (value.indexOf('FilterSettingGroup') !== -1) {
        label = facet === 'sex' ? 'Unsexed' : facet.replace(/_/g, ' ');
      }
      const dedupeKey = facet + ':' + value;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      parts.push(label);
    });

    const min = priceMin === null ? null : formatPrice(priceMin);
    const max = priceMax === null ? null : formatPrice(priceMax);
    let priceLabel = null;
    if (min && max) priceLabel = min + ' to ' + max;
    else if (min) priceLabel = min + '+';
    else if (max) priceLabel = 'under ' + max;
    if (priceLabel) parts.splice(priceSlot < 0 ? parts.length : priceSlot, 0, priceLabel);

    const extra = parts.length - MAX_LABEL_PARTS;
    const shown = parts.slice(0, MAX_LABEL_PARTS);
    if (extra > 0) shown.push('+' + extra + ' more');
    if (sortLabel) shown.push(sortLabel);
    return shown.join(' · ');
  }

  class CkResumeView extends HTMLElement {
    connectedCallback() {
      this.storeKey = this.dataset.storeKey;
      this.baseUrl = this.dataset.baseUrl || window.location.pathname;
      if (!this.storeKey) return;

      // Whether THIS page's life has seen a filtered view. Ending the page on
      // a bare view after having filtered means the shopper explicitly cleared
      // their filters, so there is nothing to resume next visit.
      this.sessionHadFilters = false;

      const current = sanitize(window.location.search);
      if (current) {
        this.save(current);
        this.sessionHadFilters = true;
      } else {
        const saved = this.load();
        if (saved) this.show(saved);
      }

      // Applying a filter in-page swaps the grid and pushes the new URL, so a
      // grid swap is the cue to re-read the location.
      const grid = document.getElementById('ProductGridContainer');
      if (grid && 'MutationObserver' in window) {
        this.observer = new MutationObserver(() => this.syncFromLocation());
        this.observer.observe(grid, { childList: true });
      }
      this.pagehideHandler = () => this.syncFromLocation();
      window.addEventListener('pagehide', this.pagehideHandler);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.pagehideHandler) window.removeEventListener('pagehide', this.pagehideHandler);
    }

    syncFromLocation() {
      const current = sanitize(window.location.search);
      if (current) {
        this.save(current);
        this.sessionHadFilters = true;
        this.hidden = true;
        return;
      }
      if (this.sessionHadFilters) this.clear();
    }

    load() {
      try {
        const raw = window.localStorage.getItem(this.storeKey);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !data.q) return null;
        if (!data.t || Date.now() - data.t > MAX_AGE_MS) {
          window.localStorage.removeItem(this.storeKey);
          return null;
        }
        return data.q;
      } catch (error) {
        return null;
      }
    }

    save(queryString) {
      try {
        window.localStorage.setItem(this.storeKey, JSON.stringify({ q: queryString, t: Date.now() }));
      } catch (error) {
        // storage unavailable (e.g. blocked third-party context); chip never shows
      }
    }

    clear() {
      try {
        window.localStorage.removeItem(this.storeKey);
      } catch (error) {
        // ignore
      }
    }

    show(queryString) {
      this.label = describe(queryString);
      if (!this.label) return;

      // Built with textContent throughout: the label echoes values from a URL
      // the shopper once visited, so it must never be parsed as HTML.
      const pill = document.createElement('div');
      pill.className = 'ck-resume__pill';
      pill.setAttribute('role', 'region');
      pill.setAttribute('aria-label', 'Resume your last view');

      const text = document.createElement('span');
      text.className = 'ck-resume__text';
      text.textContent = 'Pick up where you left off: ';
      const strong = document.createElement('strong');
      strong.textContent = this.label;
      text.appendChild(strong);

      const apply = document.createElement('a');
      apply.className = 'ck-resume__apply';
      apply.setAttribute('href', this.baseUrl + '?' + queryString + '#ProductGridContainer');
      apply.textContent = 'Resume';
      apply.addEventListener('click', () => this.capture('apply'));

      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'ck-resume__dismiss';
      dismiss.setAttribute('aria-label', 'Dismiss saved view');
      dismiss.textContent = '×';
      dismiss.addEventListener('click', () => {
        this.clear();
        this.hidden = true;
        this.capture('dismiss');
      });

      pill.appendChild(text);
      pill.appendChild(apply);
      pill.appendChild(dismiss);
      this.appendChild(pill);
      this.hidden = false;
      this.capture('show');
    }

    // Same event and schema as the quick-filter chips, under its own facet
    // name, so resume activity lands in the existing funnel.
    capture(action) {
      try {
        if (typeof KoiQuickFilters !== 'undefined' && typeof KoiQuickFilters.capture === 'function') {
          KoiQuickFilters.capture(action, 'Resume', this.label, null);
        }
      } catch (error) {
        // analytics must never break the chip
      }
    }
  }

  customElements.define('ck-resume-view', CkResumeView);
})();
